/**
 * RBAC-3.1 · Settings → Roles & Permissions: the gate, the sidebar rule and the role list.
 *
 * Pure, no DOM, no Supabase. The screen is `components/settings/roles/RolesPermissionsPage.tsx`;
 * the rows come from `rbac_account_roles()` (`20261101a9_rbac_3_1_account_roles.sql`) through
 * `services/accountRolesService.ts`.
 *
 * ⚠️ THE GATE IS `roles.administer`, NEVER `manage_roles`. `manage_roles` is read in 44 places as a
 * loose "is an admin" proxy and grants nothing here (RBAC-3.1 AC 3). The database refuses the same
 * callers this refuses: `verify_rbac_3_1_account_roles.sql` B1-B3.
 *
 * ⚠️ THE SCREEN IS ITS OWN ROUTE, `/app/settings/roles`, not `?tab=roles`. `/app/settings` sits
 * behind `SettingsGuard` (`manage_settings`), which REDIRECTS everyone else to `/`. AC 2 asks for a
 * calm no-access surface on a typed URL, and a role holding `roles.administer` without
 * `manage_settings` is exactly what duplicating Admin produces (RBAC-3.3 strips reserved
 * permissions), so the screen cannot inherit that guard.
 */

import { canAdministerRoles } from './rolesAdministration';
import { PERMISSION_AREAS, PERMISSION_DISPLAY, rendersInEditor, type PermissionArea } from './permissionDisplay';

export const ACCOUNT_ROLES_RPC = 'rbac_account_roles';

export const ROLES_SETTINGS_HREF = '/app/settings/roles';

export interface AccountRole {
  id: number;
  name: string;
  description: string | null;
  /** A Joules role: shared by every account, read only here. */
  isGlobal: boolean;
  isPrivileged: boolean;
  /** Distinct active people holding it in this account, through either role system. */
  holderCount: number;
  permissions: string[];
}

export type RolesScreenAccess = 'loading' | 'allowed' | 'denied';

export function rolesScreenAccess(input: {
  loading: boolean;
  held: Iterable<string> | null | undefined;
}): RolesScreenAccess {
  if (input.loading) return 'loading';
  return canAdministerRoles(input.held) ? 'allowed' : 'denied';
}

/**
 * Whether a Settings sidebar entry shows. The roles entry follows `roles.administer` alone; every
 * other entry follows Settings access exactly as before, so a roles-only holder sees one entry and
 * nothing that would redirect them.
 */
export function settingsEntryVisible(
  href: string,
  ctx: { canSeeSettings: boolean; canAdministerRoles: boolean },
): boolean {
  if (href === ROLES_SETTINGS_HREF) return ctx.canAdministerRoles;
  return ctx.canSeeSettings;
}

const asNumber = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** RPC rows → view model. Anything malformed reads as empty, never as a crash. */
export function parseAccountRoles(rows: unknown): AccountRole[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((r: Record<string, unknown>) => ({
    id: asNumber(r.role_id),
    name: String(r.name ?? ''),
    description: typeof r.description === 'string' ? r.description : null,
    isGlobal: r.is_global === true,
    isPrivileged: r.is_privileged === true,
    holderCount: asNumber(r.holder_count),
    permissions: Array.isArray(r.permissions) ? r.permissions.map(String) : [],
  }));
}

const byHoldersThenName = (a: AccountRole, b: AccountRole) =>
  b.holderCount - a.holderCount || a.name.localeCompare(b.name);

export interface GroupedRoles {
  own: AccountRole[];
  joules: AccountRole[];
}

/** The account's own roles above the Joules ones (AC 4), each by holders, then name. */
export function groupAccountRoles(roles: readonly AccountRole[]): GroupedRoles {
  return {
    own: roles.filter((r) => !r.isGlobal).sort(byHoldersThenName),
    joules: roles.filter((r) => r.isGlobal).sort(byHoldersThenName),
  };
}

/** The role named by `?role=`, else the first of the account's own, else the first Joules role. */
export function selectedRole(groups: GroupedRoles, requested: string | null): AccountRole | null {
  const all = [...groups.own, ...groups.joules];
  if (requested) {
    const hit = all.find((r) => String(r.id) === requested);
    if (hit) return hit;
  }
  return all[0] ?? null;
}

export function roleKindLabel(role: Pick<AccountRole, 'isGlobal'>): string {
  return role.isGlobal ? 'Joules role · read only' : 'Your role';
}

export function holderLabel(n: number): string {
  if (n <= 0) return 'Nobody in this account holds it';
  return n === 1 ? '1 person holds it' : `${n} people hold it`;
}

export interface AreaSummary {
  area: PermissionArea;
  /** Rows the editor renders in this area. */
  total: number;
  /** Labels of the rendered rows this role carries. Never keys. */
  held: string[];
}

/**
 * Per area, what the role carries out of what the editor renders. Hidden and reader-pending rows
 * are not counted, so "2 of 8" means the same eight switches RBAC-3.5's editor will show.
 */
export function areaSummaries(permissions: readonly string[]): AreaSummary[] {
  const held = new Set(permissions);
  const rows = Object.values(PERMISSION_DISPLAY).filter((d) => rendersInEditor(d.key));
  return PERMISSION_AREAS.map((area) => {
    const inArea = rows.filter((d) => d.area === area);
    return {
      area,
      total: inArea.length,
      held: inArea.filter((d) => held.has(d.key)).map((d) => d.label),
    };
  }).filter((s) => s.total > 0);
}
